import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

// 会社カレンダー。
// 「毎週土日が休み」といった基本ルールは給与設定の所定休日（曜日）で持ち、
// ここではその例外だけを登録する。祝日・年末年始・臨時休業・休日出勤日など。
// 1日ずつ全部登録すると年間300件を超え、曜日の設定と二重管理になるため。

export const DAY_KINDS = [
  { value: "legal",   label: "法定休日",   color: "#c0392b", note: "割増 1.35倍の対象" },
  { value: "holiday", label: "所定休日",   color: "#e67e22", note: "祝日・会社の休日" },
  { value: "special", label: "特別休業",   color: "#8e44ad", note: "年末年始・夏季休暇など" },
  { value: "work",    label: "営業日",     color: "#27ae60", note: "土日祝でも出勤する日" },
];

const kindOf = (v) => DAY_KINDS.find(k => k.value === v) || DAY_KINDS[1];
const pad = (n) => String(n).padStart(2, "0");
const DOW = ["日", "月", "火", "水", "木", "金", "土"];

function CompanyCalendar({ companyId, user, canEdit }) {
  const [days, setDays]       = useState([]);   // [{date, kind, name, source}]
  const [year, setYear]       = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState("");
  const [error, setError]     = useState("");
  const [message, setMessage] = useState("");
  const [adding, setAdding]   = useState(false);
  const [form, setForm]       = useState({ date: "", kind: "holiday", name: "" });
  // 祝日を休みにするか営業日にするか。取り込み時の既定になる。
  const [holidayKind, setHolidayKind] = useState("holiday");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const snap = await getDoc(doc(db, "companyCalendars", companyId));
      setDays(snap.exists() ? (snap.data().days || []) : []);
      setHolidayKind(snap.exists() ? (snap.data().holidayKind || "holiday") : "holiday");
    } catch (e) {
      console.error("会社カレンダーの取得に失敗しました:", e);
      setError("カレンダーを読み込めませんでした。");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const save = async (next, msg) => {
    setDays(next);
    try {
      await setDoc(doc(db, "companyCalendars", companyId), {
        companyId,
        days: next,
        holidayKind,
        updatedAt: Timestamp.now(),
        updatedBy: user.email,
      }, { merge: true });
      if (msg) setMessage(msg);
    } catch (e) {
      console.error("会社カレンダーの保存に失敗しました:", e);
      setError("保存に失敗しました。権限をご確認ください。");
      await load();
    }
  };

  // 内閣府のCSVから祝日を取り込む
  const importHolidays = useCallback(async (targetYear, silent) => {
    setBusy("import"); setError(""); if (!silent) setMessage("");
    try {
      const call = httpsCallable(getFunctions(undefined, "us-central1"), "fetchHolidays");
      const res = await call({ year: targetYear });
      const fetched = res.data.holidays || [];

      // 同じ日付が既にあれば、手で登録したものを優先して残す
      const manual = days.filter(d => d.source !== "内閣府" || !d.date.startsWith(String(targetYear)));
      const manualDates = new Set(manual.map(d => d.date));
      const added = fetched
        .filter(h => !manualDates.has(h.date))
        .map(h => ({ date: h.date, kind: holidayKind, name: h.name, source: "内閣府" }));

      const next = [...manual, ...added].sort((a, b) => a.date.localeCompare(b.date));
      await save(next, `${targetYear}年の祝日を${added.length}件取り込みました。`);
    } catch (e) {
      console.error("祝日の取り込みに失敗しました:", e);
      if (!silent) setError(`祝日を取り込めませんでした。${e?.message || ""}`);
    } finally {
      setBusy("");
    }
  }, [days, companyId, holidayKind]);   // eslint-disable-line react-hooks/exhaustive-deps

  // まだ取り込んでいない年があれば自動で取り込む（12月以降は翌年も）
  useEffect(() => {
    if (loading || !canEdit || busy) return;
    const now = new Date();
    const targets = [now.getFullYear()];
    if (now.getMonth() >= 2) targets.push(now.getFullYear() + 1);   // 3月以降は翌年分も公表済み
    const missing = targets.find(y => !days.some(d => d.source === "内閣府" && d.date.startsWith(String(y))));
    if (missing) importHolidays(missing, true);
  }, [loading, canEdit, days, busy, importHolidays]);

  const addDay = async () => {
    if (!form.date) { setError("日付を入力してください"); return; }
    setError("");

    // 既に登録がある日は、上書きするか確認する（祝日を営業日に変えたい場合など）
    const exists = days.find(d => d.date === form.date);
    if (exists) {
      const ok = window.confirm(
        `${form.date} は既に「${kindOf(exists.kind).label}${exists.name ? `：${exists.name}` : ""}」として登録されています。` +
        `

「${kindOf(form.kind).label}」に変更しますか？`
      );
      if (!ok) return;
    }

    const entry = { ...form, name: form.name.trim() || exists?.name || "", source: exists?.source || "手動" };
    const next = [...days.filter(d => d.date !== form.date), entry]
      .sort((a, b) => a.date.localeCompare(b.date));
    await save(next, exists ? "変更しました。" : "登録しました。");
    setForm({ date: "", kind: "holiday", name: "" });
    setAdding(false);
  };

  const changeKind = async (date, kind) => {
    await save(days.map(d => d.date === date ? { ...d, kind } : d), "区分を変更しました。");
  };

  // 祝日の扱いを切り替える。取り込み済みの祝日にも反映するか確認する。
  const changeHolidayKind = async (kind) => {
    setHolidayKind(kind);
    const imported = days.filter(d => d.source === "内閣府");
    const label = kindOf(kind).label;
    if (imported.length > 0 &&
        window.confirm(`取り込み済みの祝日${imported.length}件も「${label}」に変更しますか？\n\n「キャンセル」を選ぶと、今後取り込む分だけに適用します。`)) {
      const next = days.map(d => d.source === "内閣府" ? { ...d, kind } : d);
      setDays(next);
      try {
        await setDoc(doc(db, "companyCalendars", companyId), {
          companyId, days: next, holidayKind: kind,
          updatedAt: Timestamp.now(), updatedBy: user.email,
        }, { merge: true });
        setMessage(`祝日${imported.length}件を「${label}」に変更しました。`);
      } catch (e) {
        console.error("保存に失敗しました:", e);
        setError("保存に失敗しました。");
        await load();
      }
      return;
    }
    try {
      await setDoc(doc(db, "companyCalendars", companyId), {
        companyId, holidayKind: kind, updatedAt: Timestamp.now(), updatedBy: user.email,
      }, { merge: true });
      setMessage(`今後取り込む祝日を「${label}」として扱います。`);
    } catch (e) {
      console.error("保存に失敗しました:", e);
      setError("保存に失敗しました。");
    }
  };

  const removeDay = async (date) => {
    if (!window.confirm("この日の登録を削除しますか？")) return;
    await save(days.filter(d => d.date !== date), "削除しました。");
  };

  const yearDays = days.filter(d => d.date.startsWith(String(year)))
    .sort((a, b) => a.date.localeCompare(b.date));
  const years = [...new Set(days.map(d => d.date.slice(0, 4)))].sort();

  const byMonth = {};
  yearDays.forEach(d => {
    const m = Number(d.date.slice(5, 7));
    (byMonth[m] = byMonth[m] || []).push(d);
  });

  return (
    <div style={st.container}>
      <h2 style={st.title}>📅 会社カレンダー</h2>
      <p style={st.lead}>
        毎週の休みは給与設定の「所定休日」で決まります。ここでは祝日・年末年始・臨時休業・休日出勤日など、
        <strong>その例外だけ</strong>を登録します。ここに入れた内容が勤怠と給与計算の休日判定に使われます。
      </p>

      <div style={st.toolbar}>
        <div>
          <label style={st.label}>表示する年</label>
          <select style={st.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {[...new Set([...years, String(new Date().getFullYear()), String(new Date().getFullYear() + 1)])]
              .sort().map(y => <option key={y} value={y}>{y}年</option>)}
          </select>
        </div>
        {canEdit && (
          <div style={st.toolBtns}>
            <button style={st.importBtn} disabled={busy !== ""} onClick={() => importHolidays(year, false)}>
              {busy === "import" ? "取り込み中..." : `${year}年の祝日を取り込む`}
            </button>
            <button style={st.addBtn} onClick={() => { setAdding(true); setForm({ date: `${year}-01-01`, kind: "holiday", name: "" }); }}>
              ＋ 休日・営業日を追加
            </button>
          </div>
        )}
      </div>

      {error   && <p style={st.error}>{error}</p>}
      {message && <p style={st.ok}>{message}</p>}

      {canEdit && (
        <div style={st.kindBar}>
          <span style={st.kindBarLabel}>祝日の扱い</span>
          {[{ v: "holiday", l: "休みにする" }, { v: "work", l: "営業する" }].map(o => (
            <button key={o.v} type="button"
              style={{ ...st.kindBarBtn, ...(holidayKind === o.v ? st.kindBarOn : {}) }}
              onClick={() => changeHolidayKind(o.v)}>{o.l}</button>
          ))}
          <span style={st.kindBarNote}>
            祝日も営業する会社は「営業する」を選んでください。取り込み済みの祝日にも反映できます。
            一部の祝日だけ休む場合は、下の一覧で個別に変えられます。
          </span>
        </div>
      )}

      {canEdit && (
        <p style={st.note}>
          祝日は内閣府が公開している一覧から取り込みます。未取得の年があれば自動で取り込みます。
          法改正で祝日が変わったときは、もう一度取り込めば最新の内容に置き換わります（手で登録した日はそのまま残ります）。
        </p>
      )}

      {loading ? <p style={st.empty}>読み込み中...</p>
        : yearDays.length === 0 ? <p style={st.empty}>{year}年の登録はまだありません</p>
          : (
            <div style={st.monthGrid}>
              {Object.keys(byMonth).map(Number).sort((a, b) => a - b).map(m => (
                <div key={m} style={st.monthCard}>
                  <div style={st.monthHead}>{m}月</div>
                  {byMonth[m].map(d => {
                    const k = kindOf(d.kind);
                    const dt = new Date(d.date + "T00:00:00");
                    return (
                      <div key={d.date} style={st.dayRow}>
                        <span style={st.dayDate}>{Number(d.date.slice(8, 10))}日（{DOW[dt.getDay()]}）</span>
                        {canEdit ? (
                          <select style={{ ...st.kindSelect, color: k.color, borderColor: k.color }}
                            value={d.kind} onChange={e => changeKind(d.date, e.target.value)}>
                            {DAY_KINDS.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
                          </select>
                        ) : (
                          <span style={{ ...st.kindChip, backgroundColor: k.color }}>{k.label}</span>
                        )}
                        <span style={st.dayName}>{d.name || "－"}</span>
                        {d.source === "内閣府" && <span style={st.src}>内閣府</span>}
                        {canEdit && (
                          <button style={st.delBtn} onClick={() => removeDay(d.date)}>削除</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

      {adding && (
        <div style={st.overlay}>
          <div style={st.modal}>
            <h3 style={st.modalTitle}>休日・営業日を追加</h3>

            <label style={st.label}>日付</label>
            <input style={st.input} type="date" value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })} />

            <label style={st.label}>区分</label>
            <div style={st.kindList}>
              {DAY_KINDS.map(k => (
                <button key={k.value} type="button"
                  style={{ ...st.kindBtn, ...(form.kind === k.value ? { border: `2px solid ${k.color}`, backgroundColor: k.color, color: "#fff" } : {}) }}
                  onClick={() => setForm({ ...form, kind: k.value })}>
                  {k.label}
                  <span style={st.kindNote}>{k.note}</span>
                </button>
              ))}
            </div>

            <label style={st.label}>名称 <span style={st.opt}>（任意）</span></label>
            <input style={st.input} value={form.name} placeholder="例：夏季休暇"
              onChange={e => setForm({ ...form, name: e.target.value })} />

            <div style={st.modalBtns}>
              <button style={st.saveBtn} onClick={addDay}>追加</button>
              <button style={st.cancelBtn} onClick={() => { setAdding(false); setError(""); }}>キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** トップページに出す「今月の休日」。編集はできず、表示だけ。 */
export function ThisMonthHolidays({ companyId }) {
  const [days, setDays] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "companyCalendars", companyId));
        if (alive) setDays(snap.exists() ? (snap.data().days || []) : []);
      } catch (e) {
        console.error("会社カレンダーの取得に失敗しました:", e);
        if (alive) setDays([]);
      }
    })();
    return () => { alive = false; };
  }, [companyId]);

  if (!days || days.length === 0) return null;

  const now = new Date();
  const prefix = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const list = days.filter(d => d.date.startsWith(prefix) && d.kind !== "work")
    .sort((a, b) => a.date.localeCompare(b.date));
  if (list.length === 0) return null;

  return (
    <div style={st.banner}>
      <span style={{ fontSize: "18px" }}>📅</span>
      <span style={st.bannerLabel}>今月の休み</span>
      <span style={st.bannerBody}>
        {list.map(d => {
          const dt = new Date(d.date + "T00:00:00");
          return `${dt.getMonth() + 1}/${dt.getDate()}(${DOW[dt.getDay()]})${d.name ? ` ${d.name}` : ""}`;
        }).join("　／　")}
      </span>
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "900px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.8", marginTop: 0, marginBottom: "18px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "16px", flexWrap: "wrap", backgroundColor: "#fff", padding: "16px 20px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "12px" },
  toolBtns: { display: "flex", gap: "8px", flexWrap: "wrap" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  opt: { fontWeight: "normal", color: "#999", fontSize: "11px" },
  select: { padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", backgroundColor: "#fff" },
  importBtn: { padding: "10px 18px", backgroundColor: "#2980b9", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  addBtn: { padding: "10px 18px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "10px" },
  ok: { color: "#27ae60", fontSize: "14px", marginBottom: "10px" },
  note: { fontSize: "12px", color: "#95a5a6", lineHeight: "1.8", marginTop: 0, marginBottom: "16px" },
  empty: { color: "#999", textAlign: "center", marginTop: "32px" },
  monthGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "12px" },
  monthCard: { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", padding: "14px 16px" },
  monthHead: { fontWeight: "bold", color: "#2c3e50", marginBottom: "8px", fontSize: "15px" },
  dayRow: { display: "flex", alignItems: "center", gap: "8px", padding: "6px 0", borderTop: "1px solid #f0f0f0", flexWrap: "wrap" },
  dayDate: { fontSize: "13px", color: "#2c3e50", minWidth: "78px" },
  kindChip: { fontSize: "11px", color: "#fff", borderRadius: "10px", padding: "2px 9px", whiteSpace: "nowrap" },
  dayName: { fontSize: "13px", color: "#555", flex: 1 },
  src: { fontSize: "10px", color: "#7f8c8d", backgroundColor: "#f0f4f8", borderRadius: "8px", padding: "2px 7px" },
  delBtn: { padding: "3px 10px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", cursor: "pointer", fontSize: "11px" },
  kindSelect: { fontSize: "11px", padding: "2px 6px", borderRadius: "10px", border: "1px solid #ccc", backgroundColor: "#fff", cursor: "pointer", fontWeight: "bold" },
  kindBar: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", backgroundColor: "#eef2f6", borderRadius: "10px", padding: "12px 16px", marginBottom: "12px" },
  kindBarLabel: { fontSize: "13px", fontWeight: "bold", color: "#2c3e50" },
  kindBarBtn: { padding: "6px 16px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "13px", color: "#7f8c8d" },
  kindBarOn: { border: "2px solid #2c3e50", backgroundColor: "#2c3e50", color: "#fff", fontWeight: "bold" },
  kindBarNote: { fontSize: "12px", color: "#5d6d7e", flexBasis: "100%", lineHeight: "1.7" },
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "flex-start", zIndex: 1000, overflowY: "auto", padding: "40px 0" },
  modal: { backgroundColor: "#fff", padding: "28px 32px", borderRadius: "16px", width: "460px", maxWidth: "92vw" },
  modalTitle: { color: "#2c3e50", margin: "0 0 16px", fontSize: "18px" },
  input: { width: "100%", padding: "10px 12px", marginBottom: "14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  kindList: { display: "grid", gap: "6px", marginBottom: "14px" },
  kindBtn: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "9px 14px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#2c3e50", textAlign: "left" },
  kindNote: { fontSize: "11px", opacity: 0.8 },
  modalBtns: { display: "flex", gap: "12px", marginTop: "8px" },
  saveBtn: { padding: "10px 28px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  cancelBtn: { padding: "10px 24px", backgroundColor: "#95a5a6", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  // トップページの帯
  banner: { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", backgroundColor: "#fff", border: "1px solid #e6ebf0", borderRadius: "10px", padding: "10px 16px", marginBottom: "20px", fontSize: "13px", color: "#2c3e50" },
  bannerLabel: { fontWeight: "bold", whiteSpace: "nowrap" },
  bannerBody: { color: "#555" },
};

export default CompanyCalendar;
